import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  withDataProvider,
  TextField,
  SimpleShowLayout,
  CardActions,
  Button,
  translate as translateHOC,
} from 'react-admin';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Divider, withStyles } from '@material-ui/core';
import IconEdit from '@material-ui/icons/Edit';
import { reset } from 'redux-form';
import { BusinessDetails } from './business-detail';
import { BusinessAddress } from './business-address';
import BusinessAdditionalInfo from './business-additional-info';
import { BillingAddress } from './billing-address';
import BusinessEdit from '../edit';
import withGrants from '../../WithGrants';
import InfoPanel from '../../shared/InfoPanel';

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: `${theme.spacing.unit * 3}px`,
    padding: `${theme.spacing.unit * 2}px 0`,
  },
  cardActionsRoot: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  cardActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: `${theme.spacing.unit}px`,
    paddingBottom: theme.spacing.unit * 2,
  },
  divider: {
    width: '100%',
  },
});

const enhanceActions = compose(
  translateHOC,
  withStyles(styles)
);

const BusinessShowActions = enhanceActions(({ handleEditClick, isArchived, classes, translate }) => (
  <CardActions className={classes.cardActionsRoot}>
    <div className={classes.cardActions}>
      {isArchived && (
        <InfoPanel content={translate('resources.business.show.info.archivedBusinessUpdate')} type="info" />
      )}
      <Button onClick={handleEditClick} label="common.edit" size="medium" disabled={isArchived}>
        <IconEdit />
      </Button>
    </div>
    <Divider className={classes.divider} />
  </CardActions>
));

BusinessShowActions.propTypes = {
  handleEditClick: PropTypes.func,
  isArchived: PropTypes.bool,
};

const BusinessComment = ({ resource, record, classes }) => (
  <SimpleShowLayout resource={resource} record={record}>
    <TextField source="comment" className={classes.block} id="Selenium-businessDetails-Comment" />
  </SimpleShowLayout>
);

BusinessComment.propTypes = {
  record: PropTypes.shape({}),
  classes: PropTypes.shape({}),
  resource: PropTypes.string,
};

const BusinessShow = ({
  id,
  record,
  parentClasses,
  classes,
  checkPermission,
  permissions,
  isItaly,
  resource,
  dispatch,
  ...props
}) => {
  const [isShowDialog, setIsShowDialog] = useState(false);

  const handleEditClick = () => {
    dispatch(reset('business-details-edit'));
    setIsShowDialog(true);
  };

  const handleCloseDialog = () => setIsShowDialog(false);

  useEffect(() => {
    if (id) handleCloseDialog();
  }, [id]);

  const isAdmin = permissions === 'admin';
  const isArchived = record && record.status === 'ARCHIVED';

  return (
    <div className={classes.root}>
      {checkPermission('UPDATE_BUSINESS') && (
        <BusinessShowActions handleEditClick={handleEditClick} isArchived={isArchived} />
      )}
      <BusinessDetails resource={resource} record={record} classes={parentClasses} isAdmin={isAdmin} />
      <Divider />
      <BusinessAddress resource={resource} record={record} classes={parentClasses} isItaly={isItaly} />
      <BillingAddress resource={resource} record={record} classes={parentClasses} isItaly={isItaly} />
      <Divider />
      <BusinessAdditionalInfo resource={resource} record={record} classes={parentClasses} />
      <BusinessComment resource={resource} record={record} classes={parentClasses} />
      <Divider />
      {checkPermission('UPDATE_BUSINESS') && (
        <BusinessEdit
          {...props}
          id={id}
          record={record}
          resource={resource}
          classes={parentClasses}
          dispatch={dispatch}
          isShowDialog={isShowDialog}
          handleCloseDialog={handleCloseDialog}
        />
      )}
    </div>
  );
};

BusinessShow.propTypes = {
  id: PropTypes.string,
  dataProvider: PropTypes.func,
  dispatch: PropTypes.func,
  record: PropTypes.shape({}),
  classes: PropTypes.shape({
    root: PropTypes.string,
  }),
  parentClasses: PropTypes.shape({
    block: PropTypes.string,
    rowContent: PropTypes.string,
    inviteButton: PropTypes.string,
    field: PropTypes.string,
  }),
  resource: PropTypes.string,
  checkPermission: PropTypes.func,
  permissions: PropTypes.string,
  isItaly: PropTypes.bool,
};

const mapStateToProps = state => ({
  isItaly: state.configuration.fleet.timezone === 'Europe/Rome',
});

const enhance = compose(
  withDataProvider,
  withGrants,
  connect(mapStateToProps),
  withStyles(styles)
);

export default enhance(BusinessShow);
// For Tests
export const BusinessShowTest = BusinessShow;
